import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../utils/api';
import { toast } from 'sonner';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
  ArrowLeft,
  Save,
  Loader2
} from 'lucide-react';

const EditTemplate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    category: 'contract',
    content: '',
    description: ''
  });

  useEffect(() => {
    fetchTemplate();
  }, [id]);

  const fetchTemplate = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/contract-templates/${id}`);
      const template = res.data.template;
      
      setFormData({
        name: template.name || '',
        category: template.category || 'contract',
        content: template.content || '',
        description: template.description || ''
      });
    } catch (error) {
      console.error('Failed to load template:', error);
      toast.error('Failed to load template: ' + (error.response?.data?.detail || error.message));
      navigate('/contracts/templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.name.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    const cleanContent = formData.content.replace(/<[^>]*>/g, '').trim();
    if (!cleanContent) {
      toast.error('Please enter template content');
      return;
    }

    setSaving(true);
    try {
      await api.put(`/contract-templates/${id}`, {
        name: formData.name,
        category: formData.category,
        content: formData.content,
        description: formData.description
      });
      toast.success('Template updated successfully');
      navigate('/contracts/templates');
    } catch (error) {
      console.error('Failed to update template:', error);
      toast.error(error.response?.data?.detail || 'Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto" />
          <p className="mt-4 text-slate-600">Loading template...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/contracts/templates')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Templates
        </Button>
        <h1 className="text-3xl font-bold text-slate-800">Edit Template</h1>
        <p className="text-slate-600 mt-2">Update your contract template</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Template Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Template Name */}
          <div>
            <Label htmlFor="name">Template Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., MSP Service Agreement"
              className="mt-1"
            />
          </div>

          {/* Category */}
          <div>
            <Label htmlFor="category">Category *</Label>
            <Select
              value={formData.category}
              onValueChange={(value) => setFormData({ ...formData, category: value })}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="contract">Contract</SelectItem>
                <SelectItem value="work_order">Work Order</SelectItem>
                <SelectItem value="project_document">Project Document</SelectItem>
                <SelectItem value="amendment">Amendment</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of this template (optional)"
              rows={2}
              className="mt-1"
            />
          </div>

          {/* Content Editor */}
          <div>
            <Label>Template Content *</Label>
            <div className="mt-1 border border-slate-300 rounded-md overflow-hidden">
              <ReactQuill
                theme="snow"
                value={formData.content}
                onChange={(value) => setFormData({ ...formData, content: value })}
                style={{ minHeight: '400px' }}
                modules={{
                  toolbar: [
                    [{ 'header': [1, 2, 3, false] }],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                    [{ 'align': [] }],
                    ['link'],
                    ['clean']
                  ]
                }}
              />
            </div>
            <p className="text-sm text-slate-500 mt-2">
              Use the editor to format your template content. You can use variables like [CLIENT_NAME], [START_DATE], etc.
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-3 pt-4 border-t">
            <Button
              onClick={handleSubmit}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/contracts/templates')}
              disabled={saving}
            >
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EditTemplate;
